import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import razorpayUtils from '../utils/razorpayUtils';
import { runCashfreeCheckout } from '../checkout/strategies/CashfreeCheckout';
import { CASHFREE_CARD_ENABLED } from '../utils/cashfreeUtils';
import { API_BASE_URL } from '../config/constants';
import { useCheckoutConfig } from '../checkout/CheckoutContext';
import {
  useOrderStatusPoller,
  usePublicOrderStatusPoller,
  saveCheckoutSession,
  loadCheckoutSession,
  clearCheckoutSession,
} from '../checkout/useCheckout';
import { publicApi } from '../api/api';
import { generateUPIQRCode } from '../utils/qrCodeUtils';

import OrderSummary from '../components/checkout/OrderSummary';
import UpiIntentSection from '../components/checkout/UpiIntentSection';
import CardSection from '../components/checkout/CardSection';
import NetbankingSection from '../components/checkout/NetbankingSection';
import WalletSection from '../components/checkout/WalletSection';
import PaymentStatusModal from '../components/checkout/PaymentStatusModal';

import styles from './CheckoutPage.module.css';
import astroShubhLogo from '../assets/images/vedic-wisdom-logo.png';
import gpayLogo from '../assets/upi/gpay.svg';

const { loadRazorpaySDK, detectInAppBrowser, checkUPIAppAvailability } = razorpayUtils;

// Display names for the Cashfree status modal (UPI tile ids → label).
const UPI_APP_LABELS = { gpay: 'Google Pay', phonepe: 'PhonePe', paytm: 'Paytm', bhim: 'BHIM', upi_app: 'your UPI app' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

// UPI app → Android package name mapping for intent flow
const UPI_APP_PACKAGES = {
  gpay:    'com.google.android.apps.nbu.paisa.user',
  phonepe: 'com.phonepe.app',
  paytm:   'net.one97.paytm',
  bhim:    'in.org.npci.upiapp',
};

/**
 * Initiates payment — dispatches to Cashfree or Razorpay depending on which
 * gateway actually created THIS order (sessionData.order.gateway), not
 * whichever gateway happens to be globally active right now. This matters:
 * if an admin flips the active-gateway toggle between order-creation and the
 * customer actually paying, the payment must still go to the gateway that
 * created the order, or Cashfree/Razorpay will reject it outright.
 *
 * Cashfree path: see checkout/strategies/CashfreeCheckout.js — pure headless
 * Order Pay submission, no SDK.
 *
 * Razorpay path (unchanged from before this file supported multiple gateways):
 *
 * checkoutMode === 'custom':
 *   - Loads razorpay.js (Custom Checkout SDK)
 *   - Calls rzp.createPayment(data) with redirect:true + callback_url pointing to the
 *     backend endpoint POST /public/payment/razorpay-callback, which 302-redirects
 *     the browser to the frontend /checkout-callback with payment params as query string.
 *
 * checkoutMode === 'standard' (or anything else):
 *   - Loads checkout.js (Standard Modal SDK)
 *   - Opens rzp.open() with a handler callback — no page navigation.
 */
async function doCreatePayment({ method, app, details, sessionData, razorpayKeyId, checkoutMode }) {
  const { order, customerContact } = sessionData;

  if ((order?.gateway || 'razorpay') === 'cashfree') {
    return new Promise((resolve, reject) => {
      runCashfreeCheckout({
        order,
        method,
        app,
        details,
        customerPhone: customerContact,
        // UPI intent: no app-open signal isn't a failure — PaymentStatusModal
        // verifies via order status instead (see handlePay).
        softNotOpened: method === 'upi-intent',
        onSuccess: (resp) => resolve(resp),
        onFailure: (resp) => reject(new Error(resp?.error?.description || 'Payment failed')),
      });
    });
  }

  const isCustom = checkoutMode === 'custom';

  await loadRazorpaySDK(isCustom ? 'custom' : 'standard');

  const baseData = {
    amount:   order.amount,
    currency: order.currency || 'INR',
    order_id: order.id,
    contact:  customerContact || '',
    email:    sessionData.customerEmail || '',
    notes:    sessionData.notes || {},
  };

  let methodData = {};
  if (method === 'netbanking') {
    methodData = { method: 'netbanking', bank: details.bank };
  } else if (method === 'card') {
    const cd = details.cardDetails || {};
    methodData = {
      method:               'card',
      'card[number]':       cd.number,
      'card[name]':         cd.name,
      'card[expiry_month]': cd.expiryMonth,
      'card[expiry_year]':  cd.expiryYear,
      'card[cvv]':          cd.cvv,
    };
  } else if (method === 'wallet') {
    methodData = { method: 'wallet', wallet: details.wallet };
  } else if (method === 'upi-intent') {
    // UPI Intent: method only — app is passed as second arg to createPayment()
    methodData = { method: 'upi' };
  } else if (method === 'upi') {
    methodData = { method: 'upi', vpa: details.vpa };
  }

  return new Promise((resolve, reject) => {
    if (isCustom) {
      // Custom Checkout: createPayment + redirect to backend callback endpoint.
      // callback_url must be the backend's PUBLIC URL (ngrok or prod) — Razorpay
      // cannot reach localhost. Set REACT_APP_API_BASE_URL in .env.local to the
      // ngrok backend URL when testing locally.
      const backendUrl = API_BASE_URL.trim().replace(/\/$/, '');
      const callbackUrl = `${backendUrl}/public/payment/razorpay-callback`;
      const rzp = new window.Razorpay({ key: razorpayKeyId, redirect: true });
      rzp.on('payment.success', (resp) => resolve(resp));
      rzp.on('payment.error',   (resp) => reject(new Error(resp?.error?.description || 'Payment failed')));
      rzp.on('payment.cancel',  ()     => reject(new Error('dismissed')));
      // For UPI Intent, pass the app name as the second argument per Razorpay docs:
      // razorpay.createPayment(paymentData, { app: 'phonepe' })
      const intentOptions = (method === 'upi-intent' && app) ? { app } : undefined;
      rzp.createPayment({ ...baseData, ...methodData, callback_url: callbackUrl }, intentOptions);
    } else {
      // Standard Checkout: rzp.open() modal with method pre-filled.
      const rzp = new window.Razorpay({
        key:      razorpayKeyId,
        order_id: order.id,
        amount:   order.amount,
        currency: order.currency || 'INR',
        name:     'AstroShubh',
        prefill:  {
          name:    sessionData.customerName    || '',
          contact: customerContact             || '',
          method:  methodData.method           || method,
          ...(methodData.bank   ? { bank:   methodData.bank   } : {}),
          ...(methodData.wallet ? { wallet: methodData.wallet } : {}),
        },
        notes:   sessionData.notes || {},
        handler: (resp) => resolve(resp),
        modal:   { ondismiss: () => reject(new Error('dismissed')) },
      });
      rzp.open();
    }
  });
}

// ─── QR Overlay ───────────────────────────────────────────────────────────────

function QrOverlay({ orderId, amount, customerName, customerContact, fallbackUpiStr, onClose, onPaymentDetected }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [awaitingPayment, setAwaitingPayment] = useState(false);

  useEffect(() => {
    // Get gateway-generated QR — properly tracked so webhook fires on scan+pay.
    // customerName/customerContact added 2026-09-17: Cashfree's Create Payment
    // Link API REQUIRES customer_phone — omitting it (as this call previously
    // did) made cashfree.createPaymentLink() throw on every single call, which
    // paymentOptionsService.js silently caught and fell back to a generic local
    // "merchant@upi" QR (untracked, no real gateway link) — found live on STG
    // when the QR shown always paid to merchant@upi instead of a real Cashfree
    // QR. Razorpay's equivalent call tolerates a missing contact, which is why
    // this never surfaced before Cashfree was wired up.
    publicApi
      .post('/payment/create-qr', { orderId, amount, customerName, customerContact })
      .then((res) => {
        const url = res.data?.qrCode || res.data?.razorpayImageUrl;
        if (url) {
          setQrUrl(url);
          setAwaitingPayment(true); // QR shown — start waiting for payment
          setLoading(false);
          if (onPaymentDetected) onPaymentDetected(); // signal parent to start polling
          return;
        }
        throw new Error('No QR in response');
      })
      .catch(() => {
        // Fallback: generate client-side QR from UPI string
        generateUPIQRCode(fallbackUpiStr)
          .then((dataUrl) => {
            if (dataUrl) {
              setQrUrl(dataUrl);
              setAwaitingPayment(true);
              if (onPaymentDetected) onPaymentDetected();
            }
          })
          .finally(() => setLoading(false));
      });
  }, [orderId, amount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.qrOverlay} onClick={onClose}>
      <div className={styles.qrCard} onClick={(e) => e.stopPropagation()}>
        <button className={styles.qrClose} onClick={onClose}>✕</button>
        <h3 className={styles.qrTitle}>Scan &amp; Pay</h3>
        {loading && (
          <>
            <div className={styles.spinner} />
            <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0' }}>Generating QR code…</p>
          </>
        )}
        {!loading && qrUrl && <img src={qrUrl} alt="UPI QR Code" className={styles.qrImage} />}
        {!loading && !qrUrl && (
          <>
            <p className={styles.qrError}>Could not generate QR. Pay manually using UPI ID:</p>
            <code className={styles.upiString}>{fallbackUpiStr}</code>
          </>
        )}
        {awaitingPayment && (
          <div className={styles.qrAwaitRow}>
            <span className={styles.spinnerSmall} />
            <span>Waiting for payment… page updates automatically</span>
          </div>
        )}
        <p className={styles.qrHint}>Open GPay, PhonePe or any UPI app and scan to pay</p>
      </div>
    </div>
  );
}

// ─── Method definitions ───────────────────────────────────────────────────────
const PAYMENT_METHODS_DEF = [
  {
    id: 'upi',
    label: 'UPI',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c9a624" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 16V4m0 0L4 7m3-3 3 3"/>
        <path d="M17 8v12m0 0 3-3m-3 3-3-3"/>
      </svg>
    ),
    sub: 'GPay, PhonePe, Paytm & more',
    recommended: true,
  },
  {
    id: 'card',
    label: 'Debit/Credit Cards',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c9a624" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
    sub: 'Visa, Mastercard, RuPay',
  },
  {
    id: 'netbanking',
    label: 'Netbanking',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c9a624" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 20 7 4 7"/>
        <line x1="6" y1="7" x2="6" y2="18"/>
        <line x1="10" y1="7" x2="10" y2="18"/>
        <line x1="14" y1="7" x2="14" y2="18"/>
        <line x1="18" y1="7" x2="18" y2="18"/>
        <line x1="2" y1="18" x2="22" y2="18"/>
        <line x1="2" y1="22" x2="22" y2="22"/>
      </svg>
    ),
    sub: 'All major banks',
  },
  {
    id: 'wallet',
    label: 'Wallets',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c9a624" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 12V22H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2h14a2 2 0 0 1 2 2v4z"/>
        <path d="M20 12a2 2 0 0 0-2-2h-2v4h2a2 2 0 0 0 2-2z"/>
      </svg>
    ),
    sub: 'Paytm, Amazon Pay & more',
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    razorpayKeyId, paymentMethods, cashfreePaymentMethods, cashfreeMethodsLoaded,
    ensureCashfreeMethods, configLoading, checkoutMode, activeGateway,
  } = useCheckoutConfig();

  const [sessionData, setSessionData] = useState(null);
  const [sessionError, setSessionError] = useState(null);

  // activeTab: which accordion row is open
  const [activeTab, setActiveTab] = useState('upi');
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [openedUpiApp, setOpenedUpiApp] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [payError, setPayError] = useState('');
  // Cashfree UPI intent: { appLabel, orderId, startedAt } while the
  // "Waiting for your payment" modal is open (5-minute status window).
  const [statusModal, setStatusModal] = useState(null);

  const browserInfo = useMemo(() => detectInAppBrowser(), []);
  const deviceInfo = useMemo(() => checkUPIAppAvailability(), []);
  const { startPublicPolling: startPolling, stopPolling } = usePublicOrderStatusPoller();

  // True while a card / netbanking / wallet payment has handed the browser off to
  // the gateway's own page (full redirect). doCreatePayment never resolves on that
  // path, so `processing` stays true — and when the customer taps Back, the browser
  // restores this page from bfcache with that stale state, leaving every payment
  // method disabled until a manual refresh. Cleared once the attempt settles.
  const redirectingRef = useRef(false);

  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted && redirectingRef.current) {
        redirectingRef.current = false;
        setProcessing(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // ── Recover from in-app browser "Page can't be loaded" crash ────────────
  // When UPI intent opens an app that isn't installed, Instagram/FB WebView
  // replaces the page with an error — our JS dies before payment.error fires.
  // The flag is checked:
  //   1. On mount — covers fresh page reload
  //   2. On visibilitychange→visible — covers bfcache restore (most common in Instagram)
  //   3. On pageshow(persisted) — additional bfcache safety net
  useEffect(() => {
    const checkPending = () => {
      const pending = sessionStorage.getItem('upi_intent_pending');
      if (!pending) return;
      try {
        const { appLabel, timestamp, orderId: pendingOrderId, gateway: pendingGateway } = JSON.parse(pending);
        // Bug fix (2026-10-16): the flag was never cleared on the redirect
        // paths (Razorpay redirect:true / Cashfree https redirect — the
        // doCreatePayment promise never resolves there), so a SECOND order
        // opened within 5 minutes of a successful first one found it, took
        // the "app plausibly opened" branch below, and — since
        // loadCheckoutSession() now returns the NEW order — polled that
        // unpaid order with processing=true, disabling every payment method.
        // A flag that belongs to a different order than the one on screen is
        // never relevant here.
        const currentOrderId = location.state?.orderId || loadCheckoutSession()?.orderId;
        if (pendingOrderId && currentOrderId && String(pendingOrderId) !== String(currentOrderId)) {
          sessionStorage.removeItem('upi_intent_pending');
          return;
        }
        // Bug fix (found live on STG, 2026-09-13): no upper bound meant a
        // flag that was NEVER cleared (tab force-closed mid-payment,
        // browser killed by the OS, etc. — before it could resolve via
        // either the normal try/catch cleanup or this recovery check
        // itself) stayed in sessionStorage indefinitely. The NEXT time this
        // page mounted fresh — a completely unrelated visit, possibly a
        // different order entirely — this ran on mount and treated the
        // stale flag as "a payment might still be in flight", immediately
        // setProcessing(true)'ing and disabling every payment method
        // before the customer had touched anything. A refresh "fixed" it
        // only because this same check already deleted the flag on the
        // first load. Anything older than a few minutes is long past
        // either succeeding or failing on its own (the webhook is the
        // real source of truth either way) — just discard it silently.
        const ageMs = Date.now() - timestamp;

        // Cashfree: the browser can't tell whether the UPI app opened, so
        // never guess "not installed" — (re)open the status modal with the
        // time that's actually left and let the order status decide. The flag
        // stays until the modal reaches a terminal state (success / failed /
        // cancel), which is what removes it.
        const modalOrderId = pendingOrderId || currentOrderId;
        if (pendingGateway === 'cashfree' && modalOrderId && ageMs <= 5 * 60 * 1000) {
          setProcessing(true);
          setStatusModal((prev) => prev || {
            appLabel: UPI_APP_LABELS[appLabel] || appLabel,
            orderId: modalOrderId,
            startedAt: timestamp,
          });
          return;
        }

        if (ageMs > 5 * 60 * 1000) {
          // stale — fall through to the sessionStorage.removeItem below, no UI change
        } else if (ageMs < 20000) {
          // Quick crash — the UPI app almost certainly never opened (not installed).
          setPayError(`${appLabel || 'This app'} doesn't appear to be installed on your device. Please choose another payment option.`);
          setProcessing(false);
          setOpenedUpiApp(null);
        } else {
          // Enough time passed that the app plausibly opened and the user paid before
          // the crash — don't guess either way. Verify against our own order status
          // (the single source of truth: invoice/WhatsApp confirmation/lead-conversion
          // all happen together in the webhook that flips it) instead of silently
          // dropping a payment that may have actually gone through.
          const state = loadCheckoutSession();
          if (state?.orderId) {
            setProcessing(true);
            setStatusMessage('Checking your payment status…');
            startPolling(state.orderId, {
              onSuccess: () => {
                clearCheckoutSession();
                navigate(`/order-success/ORD_${String(state.orderId).padStart(6, '0')}`);
              },
              onFailure: () => {
                setProcessing(false);
                setStatusMessage('');
                setOpenedUpiApp(null);
                setPayError('Order processing failed. Please contact support.');
              },
              onTimeout: () => {
                // Not proof of failure — the webhook may just be running behind
                // (Razorpay retries it automatically). Tell the customer honestly
                // instead of guessing.
                setProcessing(false);
                setStatusMessage('');
                setOpenedUpiApp(null);
                setPayError("We're confirming your payment — this is taking a little longer than usual. We'll send you a confirmation on WhatsApp as soon as it's done.");
              },
            });
          }
        }
      } catch (_) {}
      sessionStorage.removeItem('upi_intent_pending');
    };

    checkPending(); // 1. fresh mount

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // Delay before checking — when the UPI app was opened and user returns,
        // Razorpay SDK fires payment.success and clears the flag within ~1-2s.
        // Checking immediately would be a false positive (flag still set, app DID open).
        // If still on this page with the flag after 3s, the app didn't open or payment failed.
        setTimeout(checkPending, 3000);
      }
    };
    const onPageShow = (e) => { if (e.persisted) checkPending(); }; // bfcache restore — check immediately

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll to top + remove body padding (mobile nav offset) on checkout ───
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = '0px';
    return () => { document.body.style.paddingBottom = prev; };
  }, []);

  // ── Load session ───────────────────────────────────────────────────────────
  useEffect(() => {
    const state = location.state || loadCheckoutSession();
    if (!state?.orderId || !state?.order) {
      setSessionError('Checkout session expired or missing. Redirecting…');
      setTimeout(() => navigate(-1), 2000);
      return;
    }
    saveCheckoutSession(state);
    setSessionData(state);
    if (state.paymentError) setPayError(state.paymentError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // This ORDER's gateway is authoritative (fixed at creation) and can differ
  // from the global active gateway the context keys its own fetch on — make
  // sure the live Cashfree bank/wallet list is fetched for a Cashfree order.
  useEffect(() => {
    if ((sessionData?.order?.gateway || '') === 'cashfree') ensureCashfreeMethods(sessionData.amount);
  }, [sessionData, ensureCashfreeMethods]);

  const handleCancel = useCallback(() => {
    stopPolling();         // marks cancelled=true — kills in-flight poll requests too
    setProcessing(false);
    setOpenedUpiApp(null);
    setStatusMessage('');
    navigate(-1);
  }, [navigate, stopPolling]);

  const handlePaymentSuccess = useCallback(() => {
    setProcessing(false);
    // Bug fix (found live on STG, 2026-09-13): this never cleared
    // openedUpiApp, so UpiIntentSection's "Opened <app>. Waiting for
    // payment…" overlay stayed stuck on screen at the same time as the
    // "Payment received! Verifying…" banner below it — confusing, since
    // polling had already started by this point.
    setOpenedUpiApp(null);
    setStatusMessage('Payment received! Verifying your order…');
    startPolling(sessionData.orderId, {
      onSuccess: () => {
        clearCheckoutSession();
        navigate(`/order-success/ORD_${String(sessionData.orderId).padStart(6, '0')}`);
      },
      onFailure: () => { setStatusMessage(''); setPayError('Order processing failed. Please contact support.'); },
      onTimeout: () => { setStatusMessage(''); setPayError('Payment verification is taking longer than expected.'); },
    });
  }, [sessionData, startPolling, navigate]);

  const handlePay = useCallback(
    async ({ method, app, ...details }) => {
      if (!sessionData) return;
      setPayError('');
      setProcessing(true);
      redirectingRef.current = method !== 'upi-intent';
      // Cashfree UPI intent shows PaymentStatusModal instead of the inline
      // "Opened <app>. Waiting…" screen (Razorpay keeps the inline screen).
      const isCashfreeUpiIntent = method === 'upi-intent'
        && (sessionData.order?.gateway || 'razorpay') === 'cashfree';
      if (method === 'upi-intent' && !isCashfreeUpiIntent) setOpenedUpiApp(app);

      // ── UPI intent "app not installed" guard ────────────────────────────────
      // When the intent URL fires and no app handles it, the browser replaces
      // the page with "Page can't be loaded" — JS dies, payment.error never fires.
      // Write a flag BEFORE firing the intent. If the page dies, the flag survives
      // in sessionStorage. The recovery useEffect finds it on Back-navigation and
      // shows the error modal.
      // NOTE: Do NOT clear this flag on visibilitychange→hidden — the page goes
      // hidden immediately when window.location fires the intent URL, even when
      // no app is installed. We can't distinguish "app opened" vs "navigation
      // started" at that point. Flag is cleared in try/catch on success or error.
      if (method === 'upi-intent') {
        sessionStorage.setItem('upi_intent_pending', JSON.stringify({
          appLabel: app,
          orderId: sessionData.orderId,
          gateway: isCashfreeUpiIntent ? 'cashfree' : 'razorpay',
          timestamp: Date.now(),
        }));
      }
      // ────────────────────────────────────────────────────────────────────────

      try {
        await doCreatePayment({ method, app, details, sessionData, razorpayKeyId, checkoutMode });
        redirectingRef.current = false; // settled without a redirect (e.g. UPI collect)
        if (isCashfreeUpiIntent) {
          // Hand over to the status modal; it owns the flag from here (removed
          // on success / failed / cancel) so a reload mid-payment resumes it.
          setStatusModal((prev) => prev || {
            appLabel: UPI_APP_LABELS[app] || 'your UPI app',
            orderId: sessionData.orderId,
            startedAt: Date.now(),
          });
          return;
        }
        sessionStorage.removeItem('upi_intent_pending');
        handlePaymentSuccess();
      } catch (err) {
        redirectingRef.current = false;
        sessionStorage.removeItem('upi_intent_pending');
        setStatusModal(null);
        setProcessing(false);
        setOpenedUpiApp(null);
        if (err.message === 'dismissed') return;
        // intent_no_apps_error: app button shown but app not actually installed
        const isNotInstalled = err.message?.toLowerCase().includes('intent_no_apps_error')
          || err.message?.toLowerCase().includes('payment failed with selected app');
        if (isNotInstalled) {
          setPayError('This app doesn\'t appear to be installed on your device. Please choose another app.');
        } else {
          setPayError(err.message || 'Payment failed. Please try again.');
        }
      }
    },
    [sessionData, razorpayKeyId, handlePaymentSuccess, checkoutMode]
  );

  const toggleTab = (id) => {
    setActiveTab((prev) => (prev === id ? null : id));
    setPayError('');
  };

  // ── Render guards ──────────────────────────────────────────────────────────
  if (sessionError) return <div className={styles.errorPage}><p>{sessionError}</p></div>;
  if (!sessionData || configLoading) {
    return <div className={styles.loadingPage}><div className={styles.spinner} /><p>Loading checkout…</p></div>;
  }

  const { amount, productName } = sessionData;
  // The order's OWN gateway (set at creation time) is authoritative — see
  // doCreatePayment's comment above for why this beats the live config value.
  const gateway = sessionData.order?.gateway || activeGateway || 'razorpay';
  const isInApp = browserInfo.isInAppBrowser;
  const isAndroid = deviceInfo.isAndroid;
  const isIOS = deviceInfo.isIOS;
  const amtDisplay = `₹${Number(amount).toLocaleString('en-IN')}`;

  // Filter methods based on paymentMethods config
  const visibleMethods = PAYMENT_METHODS_DEF.filter((m) => {
    if (m.id === 'upi') return paymentMethods?.upi?.enabled !== false;
    if (m.id === 'card') {
      if (gateway === 'cashfree' && !CASHFREE_CARD_ENABLED) return false;
      return paymentMethods?.card?.enabled !== false;
    }
    if (m.id === 'netbanking') return paymentMethods?.netbanking?.enabled !== false;
    if (m.id === 'wallet') return paymentMethods?.wallet?.enabled !== false;
    return true;
  });

  return (
    <div className={styles.page}>
      {/* ── GoKwik-style sticky header ── */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={handleCancel} aria-label="Go back">✕</button>
        <img src={astroShubhLogo} alt="AstroShubh" className={styles.topBarLogo} />
        <div className={styles.topBarRight} onClick={() => setOrderSummaryOpen(o => !o)}>
          <div className={styles.topBarAmount}>{amtDisplay}</div>
          <span className={`${styles.topBarChevron} ${orderSummaryOpen ? styles.topBarChevronOpen : ''}`}>▼</span>
        </div>
      </div>

      {/* ── Collapsible order summary ── */}
      {orderSummaryOpen && (
        <div className={styles.orderSummaryDrawer}>
          <OrderSummary productName={productName} amount={amount} />
        </div>
      )}

      {/* ── Main layout ── */}
      <div className={styles.layout}>
        <div className={styles.paymentPanel}>

          {/* RECOMMENDED OPTIONS: GPay (mobile only) */}
          {(isAndroid || isIOS) && paymentMethods?.upi?.enabled !== false && (
            <>
              <div className={styles.sectionLabel}>RECOMMENDED OPTIONS</div>
              <div className={styles.methodCard}>
                <button
                  className={styles.recommendedRow}
                  onClick={() => handlePay({ method: 'upi-intent', app: 'gpay' })}
                  disabled={processing}
                >
                  <img
                    src={gpayLogo}
                    alt="G Pay"
                    className={styles.recommendedIcon}
                  />
                  <span className={styles.recommendedName}>G Pay</span>
                  <span className={styles.recommendedAmt}>{amtDisplay} ›</span>
                </button>
              </div>
            </>
          )}

          {/* MORE OPTIONS */}
          <div className={styles.sectionLabel}>MORE OPTIONS</div>
          <div className={styles.methodCard}>
            {visibleMethods.map((m, idx) => {
              const isOpen = activeTab === m.id;
              const isLast = idx === visibleMethods.length - 1;
              return (
                <div key={m.id} className={`${styles.methodSection}${!isLast ? ' ' + styles.methodSectionBorder : ''}`}>
                  {/* ── Row header ── */}
                  <button
                    className={`${styles.methodRow} ${isOpen ? styles.methodRowActive : ''}`}
                    onClick={() => toggleTab(m.id)}
                    aria-expanded={isOpen}
                  >
                    <span className={styles.methodRowIcon}>{m.icon}</span>
                    <span className={styles.methodRowInfo}>
                      <span className={styles.methodRowLabel}>{m.label}</span>
                      <span className={styles.methodRowSub}>{m.sub}</span>
                    </span>
                    <span className={styles.methodRowRight}>
                      <span className={styles.methodRowAmount}>{amtDisplay}</span>
                      <span className={`${styles.methodChevron} ${isOpen ? styles.methodChevronOpen : ''}`}>▼</span>
                    </span>
                  </button>

                  {/* ── Expanded body ── */}
                  {isOpen && (
                    <div className={styles.methodBody}>
                      {m.id === 'upi' && (
                        <UpiIntentSection
                          gateway={gateway}
                          isAndroid={isAndroid}
                          isIOS={isIOS}
                          isInApp={isInApp}
                          inAppName={browserInfo.browserName}
                          processing={processing}
                          openedApp={openedUpiApp}
                          razorpayKeyId={razorpayKeyId}
                          amount={amount}
                          onPay={handlePay}
                          onQrRequest={() => setQrData(true)}
                          onChangeApp={() => { setOpenedUpiApp(null); stopPolling(); setStatusMessage(''); }}
                          liveWallets={cashfreePaymentMethods?.wallet || []}
                        />
                      )}
                      {m.id === 'card' && (
                        <CardSection amount={amount} processing={processing} onPay={handlePay} />
                      )}
                      {m.id === 'netbanking' && (
                        <NetbankingSection
                          amount={amount}
                          processing={processing}
                          onPay={handlePay}
                          gateway={gateway}
                          enabledBanks={paymentMethods?.netbanking?.banks || []}
                          liveBanks={cashfreePaymentMethods?.netbanking || []}
                          liveBanksLoaded={cashfreeMethodsLoaded}
                        />
                      )}
                      {m.id === 'wallet' && (
                        <WalletSection
                          amount={amount}
                          processing={processing}
                          onPay={handlePay}
                          gateway={gateway}
                          enabledWallets={paymentMethods?.wallet?.wallets || []}
                          liveWallets={cashfreePaymentMethods?.wallet || []}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Status bar — spinner only, no inline error */}
          {statusMessage && (
            <div className={styles.statusBar}>
              <div className={styles.statusMsg}>
                <span className={styles.spinnerSmall} /> {statusMessage}
              </div>
            </div>
          )}
        </div>

        {/* Desktop order summary */}
        <div className={styles.summaryPanel}>
          <OrderSummary productName={productName} amount={amount} />
        </div>
      </div>

      {/* QR overlay — shown when user clicks Show QR Code.
           Razorpay creates a tracked QR; when user pays, webhook fires qr_code.credited
           which marks the order success; polling detects it and navigates to order-success. */}
      {qrData && (
        <QrOverlay
          orderId={sessionData.orderId}
          amount={sessionData.amount}
          customerName={sessionData.customerName}
          customerContact={sessionData.customerContact}
          fallbackUpiStr={`upi://pay?pa=bansal.shubham0017-1@oksbi&pn=AstroShubh&am=${sessionData.amount}&cu=INR&tn=Order%20${sessionData.orderId}`}
          onPaymentDetected={() => {
            // Start polling — when webhook marks order 'success', we navigate
            setStatusMessage('Waiting for QR payment…');
            startPolling(sessionData.orderId, {
              onSuccess: () => {
                clearCheckoutSession();
                setQrData(null);
                navigate(`/order-success/ORD_${String(sessionData.orderId).padStart(6, '0')}`);
              },
              onFailure: () => { setStatusMessage(''); setPayError('Payment processing failed. Please contact support.'); setQrData(null); },
              onTimeout: () => { setStatusMessage(''); setQrData(null); },
            });
          }}
          onClose={() => {
            stopPolling();
            setStatusMessage('');
            setQrData(null);
          }}
        />
      )}

      {/* ── Cashfree UPI intent: waiting-for-payment modal (5-min status window) ── */}
      {statusModal && (
        <PaymentStatusModal
          orderId={statusModal.orderId}
          appLabel={statusModal.appLabel}
          startedAt={statusModal.startedAt}
          onSuccess={() => {
            sessionStorage.removeItem('upi_intent_pending');
            setStatusModal(null);
            setProcessing(false);
            clearCheckoutSession();
            navigate(`/order-success/ORD_${String(statusModal.orderId).padStart(6, '0')}`);
          }}
          onFailed={() => {
            sessionStorage.removeItem('upi_intent_pending');
            setStatusModal(null);
            setProcessing(false);
            setPayError('Payment failed. Please try again.');
          }}
          onCancel={() => {
            sessionStorage.removeItem('upi_intent_pending');
            setStatusModal(null);
            setProcessing(false);
          }}
        />
      )}

      {/* ── Error modal ── */}
      {payError && (
        <div className={styles.errorModalOverlay} onClick={() => setPayError('')}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorModalIcon}>⚠️</div>
            <p className={styles.errorModalMsg}>{payError}</p>
            <button
              className={styles.errorModalClose}
              onClick={() => setPayError('')}
            >
              OK, Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Footer — branding follows whichever gateway actually created this
           order (see `gateway` above), not just Razorpay. No Cashfree logo
           asset exists yet, so that case is text-only for now. ── */}
      <div className={styles.checkoutFooter}>
        <span>Secured by</span>
        {gateway === 'cashfree' ? (
          <span className={styles.checkoutFooterLogo} style={{ fontWeight: 600 }}>Cashfree</span>
        ) : (
          <img src={process.env.PUBLIC_URL + '/razorpay.svg'} alt="Razorpay" className={styles.checkoutFooterLogo} />
        )}
        <span className={styles.checkoutFooterDot}>•</span>
        <a
          href={gateway === 'cashfree' ? 'https://www.cashfree.com/terms-and-conditions/' : 'https://razorpay.com/terms/'}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.checkoutFooterLink}
        >
          Account &amp; Terms
        </a>
      </div>
    </div>
  );
}

export default CheckoutPage;
